/**
 * Site-spec helpers for the site designer.
 *
 * DEPENDENCY-FREE ON PURPOSE. CRA's jest cannot resolve react-router-dom v7 or
 * react-dnd, so anything importing a component is untestable here. Every piece
 * of logic worth asserting therefore lives in this module and the components
 * only render what it returns.
 *
 * The option lists mirror `jcseo/sitegen/spec.py`. They are duplicated rather
 * than fetched because they are the labels on a form control — a select that
 * cannot populate until a request lands is a worse experience than a list that
 * needs updating on the rare occasion the backend grows an option. The backend
 * validates regardless, so a stale entry here is a rejected patch, not a broken
 * site.
 */

export type PageKind = 'home' | 'single' | 'list' | 'notfound';

export const PAGE_KINDS: { value: PageKind; label: string }[] = [
  { value: 'home', label: 'Home' },
  { value: 'single', label: 'A page' },
  { value: 'list', label: 'Section index' },
  { value: 'notfound', label: '404' },
];

export const LAYOUT_MODES = [
  { value: 'docs_sidebar', label: 'Docs (sidebar)', hint: 'Sections down the left. The default for documentation.' },
  { value: 'centered_article', label: 'Centered article', hint: 'One narrow column. Good for a short, readable site.' },
  { value: 'wide_grid', label: 'Wide grid', hint: 'Full width. Suits a site that is mostly an index.' },
];

export const LIST_STYLES = [
  { value: 'cards', label: 'Cards' },
  { value: 'rows', label: 'Rows' },
  { value: 'compact', label: 'Compact list' },
];

export const DENSITIES = [
  { value: 'compact', label: 'Compact' },
  { value: 'comfortable', label: 'Comfortable' },
  { value: 'spacious', label: 'Spacious' },
];

export const RADII = [
  { value: 'none', label: 'Square' },
  { value: 'sm', label: 'Slight' },
  { value: 'md', label: 'Rounded' },
  { value: 'lg', label: 'Soft' },
  { value: 'pill', label: 'Pill' },
];

export const FONT_CHOICES = [
  'Inter',
  'Source Sans 3',
  'IBM Plex Sans',
  'Lora',
  'Source Serif 4',
  'Space Grotesk',
  'System sans',
  'System serif',
];

export const PALETTE_FIELDS: { key: string; label: string }[] = [
  { key: 'primary', label: 'Primary' },
  { key: 'accent', label: 'Accent' },
  { key: 'background', label: 'Background' },
  { key: 'surface', label: 'Surface' },
  { key: 'text', label: 'Text' },
  { key: 'muted_text', label: 'Muted text' },
  { key: 'border', label: 'Border' },
];

// Named by WHERE THE WORDMARK SITS, because that is the thing people look at
// the header and want to move. This select is the only control over it — a
// separate "brand alignment" field would be a second owner of the same fact,
// and two of these arrangements centre the wordmark by definition.
export const HEADER_LAYOUTS = [
  { value: 'brand_left', label: 'Name left · links right' },
  { value: 'brand_left_nav_left', label: 'Name left · links beside it' },
  { value: 'nav_brand_actions', label: 'Name centered · links left · buttons right' },
  { value: 'centered', label: 'Name centered, above the links' },
];

export const HEADER_STYLES = [
  { value: 'sticky', label: 'Follows the page' },
  { value: 'static', label: 'Scrolls away' },
  { value: 'hidden', label: 'No header' },
];

export const HEADER_EDGES = [
  { value: 'border', label: 'A line' },
  { value: 'shadow', label: 'A shadow' },
  { value: 'none', label: 'Nothing' },
];

export const SHADOWS = [
  { value: 'none', label: 'None' },
  { value: 'soft', label: 'Soft' },
  { value: 'strong', label: 'Strong' },
];

export type SpecFieldKind =
  | 'text'
  | 'colour'
  | 'select'
  | 'number'
  | 'toggle'
  // A list of {label, url} — the header links, the footer links. Its own kind
  // because whole-object replacement silently loses entries, which is the same
  // reason the agent has add_nav_item/remove_nav_item rather than one setter.
  | 'links'
  // A list of {label, url, emphasis}: the header buttons.
  | 'buttons';

/** The shape both link kinds edit. Matches `Link` in `jcseo/sitegen/spec.py`. */
export interface SpecLink {
  label: string;
  url: string;
  /** buttons only. */
  emphasis?: ButtonEmphasis;
}

export type ButtonEmphasis = 'primary' | 'secondary' | 'tertiary';

/**
 * How loudly a header button is drawn.
 *
 * The names are the ones design systems use; the parentheses say what the theme
 * actually does with each. One vocabulary rather than two — calling them
 * "filled/outlined/text" in the UI and "primary/secondary/tertiary" in the spec
 * would mean translating every time either is mentioned.
 */
export const BUTTON_EMPHASES: { value: ButtonEmphasis; label: string }[] = [
  { value: 'primary', label: 'Primary (filled)' },
  { value: 'secondary', label: 'Secondary (outlined)' },
  { value: 'tertiary', label: 'Tertiary (text)' },
];

export const BUTTONS_MAX = 4;

export const LINK_LABEL_MAX = 24;
export const LINK_URL_MAX = 512;
export const NAV_ITEMS_MAX = 8;

/**
 * A new row, already valid.
 *
 * Rows are never created empty. A half-filled `{label: "", url: ""}` fails the
 * backend's `Link` validation, so an editor that let you type the label first
 * would reject every save until you finished the row — and the rejection would
 * read as the panel being broken.
 */
export function newLink(label = 'New link', url = '/'): SpecLink {
  return { label, url };
}

/** A new button. Later ones default to the quiet style — a header with two
 *  equally loud buttons is almost always a mistake, and this makes the common
 *  arrangement the one you get without thinking about it. */
export function newButton(existing: number): SpecLink {
  return {
    label: existing ? 'Contact' : 'Get started',
    url: '/',
    emphasis: existing ? 'secondary' : 'primary',
  };
}

export interface SpecField {
  path: string;
  label: string;
  kind: SpecFieldKind;
  options?: { value: string; label: string }[];
  /** Colour only: an empty value is meaningful, and this says what it means. */
  emptyLabel?: string;
  min?: number;
  max?: number;
  hint?: string;
  placeholder?: string;
  /** links only: how many rows are allowed. */
  maxItems?: number;
  /** link only: what the "add" button says, and what it creates. */
  addLabel?: string;
  /**
   * select only: options that depend on the spec rather than being fixed.
   * The font lists grow when a matched site's own family is carried over, and
   * a static list would tell you a font you are legitimately using is not
   * available.
   */
  optionsFrom?: 'fonts';
}

/**
 * The families this design can actually use: the theme's eight, plus any
 * carried over from a matched site.
 *
 * A lifted family is served exactly like one of ours — same Google Fonts link,
 * built by the same function — so there is nothing second-class about it. It is
 * marked only so it is clear where it came from.
 */
export function fontOptions(spec: any): { value: string; label: string }[] {
  const lifted: any[] = dottedGet(spec, 'typography.web_fonts') || [];
  return [
    ...FONT_CHOICES.map((f) => ({ value: f, label: f })),
    ...lifted
      .filter((font) => font?.family && !FONT_CHOICES.includes(font.family))
      .map((font) => ({
        value: font.family,
        label: `${font.family} - from the matched site`,
      })),
  ];
}

/**
 * The fields the studio puts on screen, grouped the way someone thinks about
 * them rather than the way the spec is nested.
 *
 * WHY A TABLE AND NOT JSX. Everything here is assertable — that every path is
 * real, that the three colours which accept "empty" say so, that no field is
 * listed twice — and a form written directly as markup can be tested for none
 * of it, because CRA's jest cannot import a component in this project.
 *
 * Deliberately NOT every field in the spec. `build.*`, `seo.robots_noindex` and
 * the version number are not design decisions, and a panel long enough to hide
 * the colours in is a worse panel.
 */
export const SPEC_GROUPS: { title: string; note?: string; fields: SpecField[] }[] = [
  {
    title: 'Name',
    fields: [
      { path: 'site.title', label: 'Site title', kind: 'text', hint: 'The browser tab, and the wordmark when there is no logo.' },
      { path: 'site.tagline', label: 'Tagline', kind: 'text' },
      { path: 'brand.logo_text', label: 'Wordmark', kind: 'text', hint: 'Shown in the header when there is no logo image.' },
      { path: 'brand.logo_url', label: 'Logo image', kind: 'text', placeholder: 'https://…' },
      { path: 'brand.logo_height_px', label: 'Logo height', kind: 'number', min: 12, max: 64 },
      { path: 'brand.favicon_url', label: 'Favicon', kind: 'text', placeholder: 'https://…' },
    ],
  },
  {
    title: 'Colors',
    note: 'The seven the whole site is built from.',
    fields: PALETTE_FIELDS.map((f) => ({
      path: `palette.${f.key}`,
      label: f.label,
      kind: 'colour' as const,
    })),
  },
  {
    title: 'Bands',
    // The three places a site paints a block of colour that is NOT the page.
    // Grouped together because the question people actually ask is "which parts
    // are dark?", and answering it meant hunting through three different
    // sections of the spec.
    note: 'The strips of color across the top and bottom. Leave one empty to use the page color.',
    fields: [
      { path: 'layout.header_bg', label: 'Header', kind: 'colour', emptyLabel: 'Page background' },
      { path: 'home.hero_bg', label: 'Banner', kind: 'colour', emptyLabel: 'Page background', hint: 'Ignored when the banner has a picture or video in it.' },
      { path: 'layout.footer_bg', label: 'Footer', kind: 'colour', emptyLabel: 'Surface color' },
    ],
  },
  {
    title: 'Type',
    fields: [
      { path: 'typography.heading_font', label: 'Headings', kind: 'select', optionsFrom: 'fonts' },
      { path: 'typography.body_font', label: 'Body', kind: 'select', optionsFrom: 'fonts' },
      { path: 'typography.base_size_px', label: 'Body size', kind: 'number', min: 14, max: 20 },
      { path: 'typography.density', label: 'Spacing', kind: 'select', options: DENSITIES },
    ],
  },
  {
    title: 'Header',
    fields: [
      { path: 'layout.header_layout', label: 'Arrangement', kind: 'select', options: HEADER_LAYOUTS, hint: 'Where the name or logo sits, and what goes around it.' },
      { path: 'layout.header_style', label: 'On scroll', kind: 'select', options: HEADER_STYLES },
      { path: 'layout.header_edge', label: 'Bottom edge', kind: 'select', options: HEADER_EDGES },
      { path: 'layout.header_height_px', label: 'Height', kind: 'number', min: 44, max: 120 },
      { path: 'layout.header_width', label: 'Width', kind: 'select', options: [
        { value: 'contained', label: 'Held to the content' },
        { value: 'full', label: 'Edge to edge' },
      ] },
      { path: 'layout.header_float', label: 'Floating bar', kind: 'toggle' },
    ],
  },
  {
    title: 'Page',
    fields: [
      { path: 'layout.mode', label: 'Layout', kind: 'select', options: LAYOUT_MODES.map((m) => ({ value: m.value, label: m.label })) },
      { path: 'layout.container_width_px', label: 'Content width', kind: 'number', min: 720, max: 1440 },
      { path: 'layout.radius', label: 'Corners', kind: 'select', options: RADII },
      { path: 'layout.shadow', label: 'Shadows', kind: 'select', options: SHADOWS },
      { path: 'layout.list_style', label: 'Index style', kind: 'select', options: LIST_STYLES },
      { path: 'layout.show_toc', label: 'Table of contents', kind: 'toggle' },
      { path: 'layout.show_breadcrumbs', label: 'Breadcrumbs', kind: 'toggle' },
    ],
  },
  {
    title: 'Header links',
    note: 'Copied from the site you matched. Edit them freely - these are your links now.',
    fields: [
      { path: 'nav.items', label: 'Links', kind: 'links', maxItems: NAV_ITEMS_MAX },
      { path: 'nav.buttons', label: 'Buttons', kind: 'buttons', maxItems: BUTTONS_MAX, hint: 'Drawn left to right in this order. The quiet ones usually go first.' },
    ],
  },
  {
    title: 'Home',
    fields: [
      { path: 'home.hero_title', label: 'Banner headline', kind: 'text' },
      { path: 'home.hero_subtitle', label: 'Banner text', kind: 'text' },
      { path: 'home.hero_image_url', label: 'Banner picture', kind: 'text', placeholder: 'https://…' },
      { path: 'home.hero_video_url', label: 'Banner video', kind: 'text', placeholder: 'https://…', hint: 'Plays muted and looping. Wins over the picture, which becomes its poster frame.' },
      { path: 'home.hero_overlay', label: 'Darken the picture', kind: 'number', min: 0, max: 0.85, hint: '0 to 0.85. Only applies when there is a picture.' },
      { path: 'home.sections_heading', label: 'Sections heading', kind: 'text' },
    ],
  },
  {
    title: 'Footer',
    fields: [
      { path: 'footer.text', label: 'Footer text', kind: 'text' },
      { path: 'footer.links', label: 'Links', kind: 'links', maxItems: NAV_ITEMS_MAX },
    ],
  },
];

/**
 * Is this field still at its default?
 *
 * Compared by VALUE, not identity: two of the fields are a list and two are a
 * nullable object, and `[] !== []`. Missing and default are the same answer —
 * a spec saved before a field existed has no key for it and is, correctly, at
 * that field's default.
 */
export function isAtDefault(spec: any, defaults: any, path: string): boolean {
  if (!defaults) return true;
  const mine = dottedGet(spec, path);
  const theirs = dottedGet(defaults, path);
  if (mine === undefined || mine === null || mine === '') {
    return theirs === undefined || theirs === null || theirs === '';
  }
  return JSON.stringify(mine) === JSON.stringify(theirs);
}

/** The paths in a group that have been moved off their defaults. */
export function movedPaths(spec: any, defaults: any, paths: string[]): string[] {
  return paths.filter((path) => !isAtDefault(spec, defaults, path));
}

/** One field the agent moved, ready to show in the transcript. */
export interface SpecChange {
  path: string;
  label: string;
  value: string;
}

/** A field's value written the way its control shows it, not the way it is stored. */
export function describeValue(field: SpecField, value: any): string {
  if (value === undefined || value === null || value === '') {
    return field.emptyLabel || 'none';
  }
  if (field.kind === 'toggle') return value ? 'on' : 'off';
  if (field.kind === 'select') {
    return (field.options || []).find((o) => o.value === value)?.label || String(value);
  }
  if (field.kind === 'links' || field.kind === 'buttons') {
    const rows = Array.isArray(value) ? value : [];
    const noun = field.kind === 'buttons' ? 'buttons' : 'links';
    if (!rows.length) return 'none';
    return rows.length === 1 ? rows[0].label : `${rows.length} ${noun}`;
  }
  const text = String(value);
  // A URL says more from its end than its start — every one of them begins
  // "https://" and the part that identifies it is the filename.
  if (text.length > 28) {
    return field.placeholder === 'https://…' ? `…${text.slice(-24)}` : `${text.slice(0, 26)}…`;
  }
  return text;
}

/**
 * What moved between two specs, in the panel's own words.
 *
 * THIS IS THE POINT OF THE WHOLE TAB ARRANGEMENT. The fields panel earned its
 * keep partly by letting you watch values move as the agent worked; putting it
 * behind a tab would take that away. Showing the same information in the
 * transcript — where you already are while the agent works — gives it back, and
 * gives it back BETTER: a named list of what changed beats noticing a colour
 * shift in a rail out of the corner of your eye.
 */
export function describeChanges(before: any, after: any): SpecChange[] {
  if (!before || !after) return [];
  const changes: SpecChange[] = [];
  SPEC_GROUPS.forEach((group) =>
    group.fields.forEach((field) => {
      const was = dottedGet(before, field.path);
      const now = dottedGet(after, field.path);
      if (JSON.stringify(was ?? null) === JSON.stringify(now ?? null)) return;
      changes.push({
        path: field.path,
        label: field.label,
        value: describeValue(field, now),
      });
    }),
  );
  return changes;
}

/** Who made a version, in words rather than an enum value. */
export function versionAuthor(authoredBy: string): string {
  const known: Record<string, string> = { HUMAN: 'You', AI: 'The designer' };
  return known[(authoredBy || '').toUpperCase()] || authoredBy || 'Unknown';
}

/**
 * How far a version sits from the design as it stands.
 *
 * Shown per row so the list is a set of CHOICES rather than a set of
 * timestamps: "12 fields differ" tells you whether a row is worth looking at,
 * where "22 Aug, 14:03" does not.
 */
export function versionDistance(versionSpec: any, currentSpec: any): string {
  const changes = describeChanges(versionSpec, currentSpec);
  if (!changes.length) return 'Same as now';
  return changes.length === 1
    ? `1 field differs · ${changes[0].label}`
    : `${changes.length} fields differ`;
}

/** Every path the panel edits, for asserting they all exist in the spec. */
export function specFieldPaths(): string[] {
  return SPEC_GROUPS.flatMap((g) => g.fields.map((f) => f.path));
}

/**
 * Coerce what a form control produced into what the backend's validator wants.
 *
 * The number inputs are the reason this exists: an `<input type=number>` reports
 * `""` while you are clearing it and `"17"` the rest of the time, and both go to
 * a pydantic `int` field that rejects a string outright.
 */
export function coerceFieldValue(field: SpecField, raw: any): any {
  if (field.kind === 'number') {
    if (raw === '' || raw === null || raw === undefined) return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  }
  if (field.kind === 'toggle') return Boolean(raw);
  // Links pass through as-is — the row editor only ever hands over complete
  // objects, and `null` is a meaningful value for a button that was removed.
  return raw;
}

/**
 * Which paths a brand profile owns — the ones a brand switch repaints.
 * Mirrors `BRAND_OWNED_PATHS` in `jcseo/sitegen/spec.py`; shown to the user
 * before they agree to it, because everything else they have done survives.
 */
export const BRAND_OWNED_LABELS = [
  'the primary, accent, background and text colors',
  'the wordmark and logo',
  'the fonts',
  'the footer text',
];

/** Read a dotted path out of a spec. Returns undefined rather than throwing. */
export function dottedGet(spec: any, path: string): any {
  if (!spec || !path) return undefined;
  return path.split('.').reduce((node, part) => (node == null ? undefined : node[part]), spec);
}

/**
 * Write a dotted path, returning a NEW object.
 *
 * Non-mutating because React state is compared by identity — mutating in place
 * updates the spec and never re-renders the preview, which reads as "the app
 * ignored my change".
 */
export function dottedSet(spec: any, path: string, value: any): any {
  const parts = path.split('.');
  const next = { ...(spec || {}) };
  let node: any = next;
  for (let i = 0; i < parts.length - 1; i += 1) {
    node[parts[i]] = { ...(node[parts[i]] || {}) };
    node = node[parts[i]];
  }
  node[parts[parts.length - 1]] = value;
  return next;
}

/** Hex with a leading '#', 3 or 6 digits — what the backend's Palette accepts. */
export function isHex(value: string): boolean {
  return /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value || '');
}

/**
 * Relative luminance, for deciding whether a swatch needs light or dark text —
 * and for the contrast warning, which is the one bit of taste worth enforcing
 * in the UI rather than leaving to the agent.
 */
export function luminance(hex: string): number {
  if (!isHex(hex)) return 0;
  let v = hex.slice(1);
  if (v.length === 3) v = v.split('').map((c) => c + c).join('');
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(v.slice(i, i + 2), 16));
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

/**
 * WCAG relative luminance, with the sRGB gamma expansion.
 *
 * NOT the same as `luminance` above, and the difference decides real cases.
 * `luminance` averages the raw channel bytes — monotonic, which is all the
 * light-or-dark swatch decision needs. Contrast needs the gamma step: without
 * it dark colours read as far lighter than they are, and `#414552` on white
 * scores 3.3 against its true 9.6 — so a perfectly readable palette gets
 * flagged as failing.
 */
function relLuminance(hex: string): number {
  if (!isHex(hex)) return 0;
  let v = hex.slice(1);
  if (v.length === 3) v = v.split('').map((c) => c + c).join('');
  const chan = (i: number) => {
    const c = parseInt(v.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * chan(0) + 0.7152 * chan(2) + 0.0722 * chan(4);
}

/**
 * Text-on-background contrast, WCAG's ratio. Below 4.5 fails AA for body text.
 * Reported rather than blocked: someone may be mid-edit, and refusing to save a
 * half-finished palette is worse than saying it is currently unreadable.
 */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [relLuminance(a), relLuminance(b)].sort((x, y) => y - x);
  return Number(((hi + 0.05) / (lo + 0.05)).toFixed(2));
}

export function contrastWarning(spec: any): string | null {
  const text = dottedGet(spec, 'palette.text');
  const background = dottedGet(spec, 'palette.background');
  if (!isHex(text) || !isHex(background)) return null;
  const ratio = contrastRatio(text, background);
  if (ratio >= 4.5) return null;
  return `Body text contrast is ${ratio}:1 - below the 4.5:1 needed to stay readable.`;
}

/** A one-line description of a design, for a header or a version row. */
export function specSummary(spec: any): string {
  if (!spec || !spec.layout) return 'No design yet';
  const mode = LAYOUT_MODES.find((m) => m.value === spec.layout.mode);
  const bits = [
    mode ? mode.label : spec.layout.mode,
    dottedGet(spec, 'typography.heading_font'),
    dottedGet(spec, 'palette.primary'),
  ].filter(Boolean);
  const navCount = (dottedGet(spec, 'nav.items') || []).length;
  bits.push(navCount === 1 ? '1 nav item' : `${navCount} nav items`);
  return bits.join(' · ');
}

/** Has the draft moved since the last publish? Mirrors the server's derivation. */
export function isSpecAhead(spec: any, publishedSpec: any): boolean {
  if (!publishedSpec) return Boolean(spec && Object.keys(spec).length);
  return JSON.stringify(spec || {}) !== JSON.stringify(publishedSpec || {});
}

/**
 * Normalise what someone pastes into the "match this site" box.
 *
 * People paste `acme.com`, `www.acme.com/docs`, a URL with a trailing space, or
 * the whole thing wrapped in quotes from a chat message. Rejecting those as
 * "not a URL" is a worse answer than adding the scheme they meant.
 * Returns null when there is nothing plausibly a host in there.
 */
export function normalizeReferenceUrl(raw: string): string | null {
  let value = (raw || '').trim().replace(/^["'<]+|["'>]+$/g, '');
  if (!value) return null;
  if (!/^https?:\/\//i.test(value)) value = `https://${value}`;
  try {
    const url = new URL(value);
    // A host with no dot is a typo or a local name, not a site to copy.
    if (!url.hostname.includes('.')) return null;
    return url.toString();
  } catch {
    return null;
  }
}

/**
 * What the match button should say.
 *
 * The two presses do different work — the first LIFTS a site's design, the
 * second LOOKS at the result beside it and closes the gap — so the button says
 * which one you are about to get. Pointing it at a different address goes back
 * to matching, because there is nothing yet to refine against that site.
 */
export function matchButtonLabel(savedUrl: string | null | undefined, input: string): string {
  const target = normalizeReferenceUrl(input);
  const saved = normalizeReferenceUrl(savedUrl || '');
  return saved && target === saved ? 'Refine the match' : 'Match this site';
}

/**
 * The instruction for a REFINE press: look, don't re-read.
 *
 * Re-lifting would overwrite the design with the same measurements it already
 * has and throw away everything the eye and the user changed since. The second
 * press is about the gap between the two renders, which only the eye can see.
 */
export function refineMatchPrompt(url: string, kind: PageKind = 'home'): string {
  // The page kind is named because the comparison photographs ONE of our pages,
  // and until it was passed it always photographed the home page — so refining
  // while looking at the article template compared the wrong two things and
  // reported differences that were really just the two page kinds differing.
  const label = PAGE_KINDS.find((p) => p.value === kind)?.label || 'Home';
  const aside =
    kind === 'home'
      ? ''
      : ` I am looking at the ${label.toLowerCase()} page, so pass kind="${kind}" - and if ${url} is that site's home page, use the address of a comparable page there instead.`;
  return (
    `Call compare_with_site on ${url} to see how close this is, and apply what ` +
    `it finds.${aside} If it reports more to do, call it again - two or three rounds is ` +
    `normal. Then tell me what changed and what is still different. Do not ` +
    `re-lift the site; the colors are already measured.`
  );
}

/** The instruction sent to the agent when someone submits a reference URL. */
export function matchSitePrompt(url: string): string {
  // Takes the NAVIGATION too. An earlier version told the agent to leave it
  // alone, which quietly defeated the point: a docs subdomain reads as part of
  // the main site because it carries the same header, links included. The
  // layout MODE stays put — that is about this site's own structure, not the
  // reference's.
  return (
    `Look at ${url} and match its design as closely as you can: call ` +
    `lift_style_from_url on it, then apply_lifted_tokens with no arguments so ` +
    `the palette, typography, brand, header and navigation all come across. ` +
    `Then tell me what you took and anything you could not read. Keep the ` +
    `layout mode as it is.`
  );
}

/**
 * The widths the preview renders at.
 *
 * Desktop is 1440 because that is the viewport the design probe measures a
 * reference site at — so what you see here is the width the lifted numbers
 * describe. It is NOT "however wide the pane happens to be": the preview pane is
 * half the studio, so a full-width iframe rendered NARROWER than the tablet
 * setting and the site's own container width could never be reached at all.
 */
export const PREVIEW_WIDTHS: { value: string; label: string; px: number }[] = [
  { value: 'desktop', label: 'Desktop', px: 1440 },
  { value: 'tablet', label: 'Tablet', px: 834 },
  { value: 'mobile', label: 'Phone', px: 390 },
];

/**
 * How much to shrink the frame so a real 1440px page fits the pane.
 *
 * Scaling down beats narrowing the frame: at 750px the site would take its
 * tablet layout and you would be reviewing a design nobody is going to see on a
 * laptop. Never scales UP — a phone frame blown up to fill the pane would show
 * text at a size the phone never renders.
 */
/**
 * Which colour scheme the preview renders in.
 *
 * The published site has no toggle — it follows `prefers-color-scheme`, because
 * a switch needs JavaScript and the preview iframe runs with `sandbox=""`. That
 * makes this a STUDIO control, not a site setting: someone on a dark laptop was
 * shown a dark preview while editing the light palette in the panel beside it,
 * with no way to see what they were changing.
 */
export type PreviewScheme = 'auto' | 'light' | 'dark';

export const PREVIEW_SCHEMES: { value: PreviewScheme; label: string; hint: string }[] = [
  { value: 'auto', label: 'Auto', hint: 'What a visitor sees: their own system setting.' },
  { value: 'light', label: 'Light', hint: 'Force the light palette, whatever this machine is set to.' },
  { value: 'dark', label: 'Dark', hint: 'Force the dark palette, which is derived from your colors.' },
];

export function previewScale(frameWidth: number, availableWidth: number): number {
  if (!frameWidth || !availableWidth || availableWidth <= 0) return 1;
  return Math.min(1, Number((availableWidth / frameWidth).toFixed(4)));
}

/**
 * Where the full-size preview lives.
 *
 * A ROUTE IN THIS APP, not a `blob:` URL of the rendered HTML. The blob was
 * how this worked while the preview was something to look at: the endpoint is
 * authenticated and `window.open` cannot carry a bearer token, so the HTML was
 * fetched here and handed over as a blob. But a blob is a dead document — no
 * session to fetch the next page with, no parent for its click handler to talk
 * to — so once the preview became browsable it could be looked at and not read,
 * and any link with a real destination walked you off to the live site.
 */
export function previewTabUrl(
  siteId: number,
  kind: PageKind,
  scheme: PreviewScheme,
): string {
  return `/deliver/${siteId}/design/preview?kind=${encodeURIComponent(
    kind,
  )}&scheme=${encodeURIComponent(scheme)}`;
}

/**
 * The sandbox the preview frame runs under, everywhere it is embedded.
 *
 * `allow-scripts` WITHOUT `allow-same-origin` is the whole safety story: the
 * frame gets an opaque origin, so its script cannot read our cookies, our
 * storage or our document — all it can do is post a message. That matters more
 * here than in most frames, because the markup inside it includes a wordmark
 * lifted verbatim from somebody else's site.
 *
 * Nothing else is granted. `allow-popups` and `allow-popups-to-escape-sandbox`
 * were here so an off-site link could open a real tab; the frame now posts
 * those up instead and the studio opens them, which costs a line and takes two
 * grants away from the one frame in this app that renders scraped markup.
 */
export const PREVIEW_SANDBOX = 'allow-scripts';

/** What a click inside the preview frame asked for. */
export type PreviewClick =
  | { kind: 'path'; path: string }
  | { kind: 'external'; url: string };

/** Schemes the studio will open on the frame's behalf. */
const EXTERNAL_SCHEME = /^(https?|mailto|tel):/i;

/**
 * Is this message from a frame WE embedded?
 *
 * `window.addEventListener('message')` hears every message posted to this page:
 * from an embedder, from a window we opened, from any frame. Without this, a
 * message shaped like the preview's could drive the studio's rendering and URL
 * from anywhere. `window[i]` for `i < window.length` is exactly the list of
 * this document's own child frames.
 */
export function isOwnFrame(source: unknown, win: Window = window): boolean {
  try {
    for (let i = 0; i < win.length; i += 1) {
      if ((win as any)[i] === source) return true;
    }
  } catch {
    return false;
  }
  return false;
}

/** The payload half of the check above — what the frame is asking for. */
export function readPreviewMessage(data: any): PreviewClick | null {
  if (!data || data.source !== 'site-preview') return null;
  if (typeof data.path === 'string' && data.path) {
    return { kind: 'path', path: data.path };
  }
  if (typeof data.external === 'string' && data.external) {
    const url =
      data.external.slice(0, 2) === '//' ? `https:${data.external}` : data.external;
    // A SCHEME ALLOWLIST, because this URL came out of a header we scraped and
    // we are about to hand it to `window.open` on OUR origin. `javascript:` is
    // the one that matters and it is not on the list.
    return EXTERNAL_SCHEME.test(url) ? { kind: 'external', url } : null;
  }
  return null;
}

/** Quick-prompt chips. Ordered so the first is whatever this site needs next. */
export function starterPrompts(spec: any, isPublished: boolean): string[] {
  const prompts: string[] = [];
  if (!dottedGet(spec, 'site.tagline')) {
    prompts.push('Write a title and tagline for this site');
  }
  prompts.push('Copy the colors and fonts from ');
  prompts.push('Make it darker');
  if (!(dottedGet(spec, 'nav.items') || []).length) {
    prompts.push('Add a header link back to the main site');
  }
  if (dottedGet(spec, 'layout.mode') !== 'docs_sidebar') {
    prompts.push('Switch to the docs sidebar layout');
  }
  if (!isPublished) prompts.push('What will this put in my repository?');
  return prompts.slice(0, 5);
}
